'use client';

import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, User, Phone, CheckCircle2, XCircle, AlertCircle, Trash2, Bell, ShieldCheck, PlayCircle } from 'lucide-react';
import { Appointment } from './types';

interface BookingDetailModalProps {
  appointment: Appointment | null;
  onClose: () => void;
  onUpdateStatus: (appointmentId: string, newStatus: string) => void;
}

export const BookingDetailModal: React.FC<BookingDetailModalProps> = ({
  appointment,
  onClose,
  onUpdateStatus,
}) => {
  const [updating, setUpdating] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [processingNotif, setProcessingNotif] = useState(false);

  useEffect(() => {
    if (appointment?.id) {
      setLoadingNotifs(true);
      fetch(`/api/cron/notifications?appointmentId=${appointment.id}`)
        .then(r => r.json())
        .then(data => {
          if (data.success && data.notifications) {
            setNotifications(data.notifications);
          }
        })
        .catch(err => console.warn('Error fetching notifications for appointment:', err))
        .finally(() => setLoadingNotifs(false));
    }
  }, [appointment?.id]);

  if (!appointment) return null;

  const handleAction = async (status: string) => {
    setUpdating(true);
    await onUpdateStatus(appointment.id, status);
    setUpdating(false);
    onClose();
  };

  const handleRunNotifications = async () => {
    setProcessingNotif(true);
    try {
      const res = await fetch('/api/cron/notifications', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        // Refrescar lista de notificaciones del turno
        const updatedRes = await fetch(`/api/cron/notifications?appointmentId=${appointment.id}`);
        const updatedData = await updatedRes.json();
        if (updatedData.success) {
          setNotifications(updatedData.notifications);
        }
      }
    } catch (e) {
      console.warn('Error running notifications worker:', e);
    } finally {
      setProcessingNotif(false);
    }
  };

  const isDepositPaid = String(appointment.paymentStatus || '').toLowerCase() === 'deposit_paid';
  const hasDeposit = (appointment.depositAmount || 0) > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 flex flex-col gap-4 animate-fade-in max-h-[90vh] overflow-y-auto">
        {/* Cabecera */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div>
            <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
              DETALLE DEL TURNO
            </span>
            <h2 className="text-lg font-bold text-[#0f172a]">
              #{appointment.id.slice(0, 10)}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Datos Principales */}
        <div className="flex flex-col gap-3 text-xs">
          {/* Cliente */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs">
                <User className="w-4 h-4" />
              </div>
              <div>
                <span className="font-extrabold text-sm text-[#0f172a] block">
                  {appointment.clientName}
                </span>
                <span className="text-slate-500 block">{appointment.clientPhone}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {appointment.isOverturn && (
                <span className="bg-amber-100 text-amber-800 text-[10px] font-black px-2 py-0.5 rounded-full uppercase border border-amber-300">
                  Sobreturno
                </span>
              )}
              {appointment.securityCode && (
                <span className="bg-slate-200 text-slate-700 text-[10px] font-mono px-2 py-0.5 rounded font-bold">
                  PIN: #{appointment.securityCode}
                </span>
              )}
            </div>
          </div>

          {/* Servicio y Staff */}
          <div className="grid grid-cols-2 gap-2.5">
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase block">
                Servicio
              </span>
              <span className="font-bold text-[#0f172a] block mt-0.5">
                {appointment.serviceName}
              </span>
              <span className="text-slate-400 text-[10px] block mt-0.5">
                {appointment.duration} min
              </span>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase block">
                Profesional
              </span>
              <span className="font-bold text-[#0f172a] block mt-0.5">
                {appointment.staffName}
              </span>
              <span className="text-slate-400 text-[10px] block mt-0.5">
                Asignado
              </span>
            </div>
          </div>

          {/* Fecha, Hora e Importe */}
          <div className="grid grid-cols-2 gap-2.5">
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase block">
                Fecha y Horario
              </span>
              <span className="font-bold text-[#0f172a] block mt-0.5">
                {appointment.dateStr}
              </span>
              <span className="text-emerald-700 font-extrabold text-[11px] block mt-0.5">
                {appointment.startTime} - {appointment.endTime} Hs
              </span>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase block">
                Importe y Cobro
              </span>
              <span className="text-base font-black text-[#0f172a] block mt-0.5">
                ${' '}
                {appointment.price?.toLocaleString('es-AR', {
                  minimumFractionDigits: 0,
                })}
              </span>
              <div className="mt-1">
                {isDepositPaid ? (
                  <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-300">
                    <ShieldCheck className="w-3 h-3 text-emerald-600" />
                    Seña abonada: ${appointment.depositAmount?.toLocaleString('es-AR')}
                  </span>
                ) : hasDeposit ? (
                  <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-300">
                    Seña pendiente: ${appointment.depositAmount?.toLocaleString('es-AR')}
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-400 block">
                    Sin seña anticipada
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Desglose Multi-servicio si existen varios ítems */}
          {appointment.items && appointment.items.length > 1 && (
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1.5">
                Servicios del Turno ({appointment.items.length})
              </span>
              <div className="space-y-1.5">
                {appointment.items.map((it: any) => (
                  <div key={it.id || it.serviceId} className="flex justify-between items-center text-xs">
                    <span className="font-semibold text-slate-800">{it.name || it.serviceName} ({it.duration} min)</span>
                    <span className="font-bold text-[#0f172a]">${it.price?.toLocaleString('es-AR')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notificaciones y Recordatorios Programados */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Bell className="w-3.5 h-3.5 text-emerald-600" />
                <span>Recordatorios y Alertas ({notifications.length})</span>
              </span>
              <button
                type="button"
                disabled={processingNotif}
                onClick={handleRunNotifications}
                className="text-[10px] font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 cursor-pointer disabled:opacity-50"
              >
                <PlayCircle className="w-3 h-3" />
                <span>{processingNotif ? 'Procesando...' : 'Ejecutar pendientes'}</span>
              </button>
            </div>

            {loadingNotifs ? (
              <p className="text-[11px] text-slate-400">Consultando trabajos programados...</p>
            ) : notifications.length === 0 ? (
              <p className="text-[11px] text-slate-400">No hay recordatorios registrados para esta reserva.</p>
            ) : (
              <div className="space-y-1.5 pt-1">
                {notifications.map((n) => {
                  const isSimulated = n.status === 'SIMULATED';
                  const isPending = n.status === 'PENDING';
                  const isCancelled = n.status === 'CANCELLED';
                  const isFailed = n.status === 'FAILED';

                  const badgeClass = isSimulated
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                    : isPending
                    ? 'bg-amber-100 text-amber-800 border-amber-300'
                    : isCancelled
                    ? 'bg-slate-200 text-slate-600 border-slate-300'
                    : 'bg-rose-100 text-rose-800 border-rose-300';

                  const statusLabel = isSimulated
                    ? 'Simulado (Pruebas)'
                    : isPending
                    ? 'Programado'
                    : isCancelled
                    ? 'Cancelado'
                    : 'Fallido';

                  return (
                    <div
                      key={n.id}
                      className="p-2 bg-white rounded-xl border border-slate-200/70 flex items-center justify-between text-[11px]"
                    >
                      <div>
                        <strong className="text-slate-800 block">{n.type.replace(/_/g, ' ')}</strong>
                        <span className="text-slate-400 text-[10px]">
                          {new Date(n.scheduledFor).toLocaleString('es-AR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })} hs
                        </span>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full font-bold text-[9px] uppercase border ${badgeClass}`}>
                        {statusLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notas si existen */}
          {appointment.notes && (
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 text-[11px] text-slate-600">
              <span className="font-bold block text-slate-400 uppercase text-[9px]">
                Notas
              </span>
              <p className="mt-0.5">{appointment.notes}</p>
            </div>
          )}
        </div>

        {/* Acciones Rápidas */}
        <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
          <div className="grid grid-cols-2 gap-2">
            <button
              disabled={updating}
              onClick={() => handleAction('completed')}
              className="bg-[#047857] hover:bg-[#065f46] text-white py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition cursor-pointer"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Marcar Asistió</span>
            </button>

            <button
              disabled={updating}
              onClick={() => handleAction('no_show')}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition cursor-pointer"
            >
              <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
              <span>No vino</span>
            </button>
          </div>

          <button
            disabled={updating}
            onClick={() => handleAction('cancelled')}
            className="text-rose-600 hover:bg-rose-50 border border-rose-200 py-2 rounded-xl font-semibold text-xs flex items-center justify-center gap-1.5 transition cursor-pointer"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>Cancelar turno</span>
          </button>
        </div>
      </div>
    </div>
  );
};
